import { LitElement, html, css, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";

import { imagesObjectURLMap, loadFonts, setSharedStyles } from "./shared";
import { idml2docbook } from "../idml2docbook/idml2docbookServer";
import { fileTypeToPandocType, InputFormat, OutputFormat, PandocJson, pandocServer } from "../pandoc";
import { AstRenderer, OutDesignAST, pandocToOutDesign, outDesignToPandoc } from "../ast";
import { rolesToClasses } from "../rules";

import { OutDesignBlockElement } from "./OutDesignBlockElement";
import { OutDesignFooter } from "./FooterElement";
import { getManifest, isDebugModeOn, sanitize, stringifyStyleSheet } from "./utils";

import '@awesome.me/webawesome/dist/components/progress-bar/progress-bar.js';

import '@vaadin/upload';
import { UploadFilesChangedEvent } from "@vaadin/upload"
import { generateConverter } from "./generate-converter";

import pako from "pako";

AstRenderer;
OutDesignBlockElement;
OutDesignFooter;

@customElement("od-main-window")
export class OutDesignMainWindow extends LitElement {
    @state() private file?: File; // The file to convert

    private inputFormat?: "idml" | InputFormat; // The source format
    private outputFormat?: OutputFormat; // The format decided when export generator is clicked

    private idml2docbookOptions?: Record<string, string | boolean>; // The source format
    private files?: Record<string, Array<File>>;
    private uploadDebounceTimer: number | undefined;

    @state() private converting = false;
    private conversionTimeStart: DOMHighResTimeStamp;
    @state() private conversionProgress: number = -1;
    
    @state() private docbookOutput = "";
    @state() private cssOutput: CSSStyleSheet = new CSSStyleSheet();
    @state() private pandocJson: PandocJson | undefined;
    @state() private outDesignAst: OutDesignAST | undefined;

    private defaultIdml2docbookOptions = { // copied from a real response
        "typography": true,
        "thin_spaces": true,
        "linebreaks": true,
        "ignore_overrides": false,
        "media": "."
    }

    private acceptedMimeTypes: Record<string, Array<string>> = {
        documents: [
            ".idml",                                   // IDML
            ".adoc",                                   // Asciidoc
            ".asc",                                    // Asciidoc
            ".asciidoc",                               // Asciidoc
            "text/x-tex",                              // TEX
            "application/x-docbook+xml",               // Docbook
            "application/xml",                         // XML (Docbook?)
            "text/html",                               // HTML
            ".md",                                     // MD
            "text/markdown",                           // MD
            "application/vnd.oasis.opendocument.text", // ODT
            "application/epub+zip",                    // EPUB
            "application/rtf",                         // RTF
            "application/vnd.openxmlformats-officedocument.presentationml.presentation", // PPTX
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",   // DOCX
        ],
        images: [
            "image/jpeg",                              // JPEG
            "image/png",                               // PNG
            "image/tiff",                              // TIFF
            "image/svg+xml",                           // SVG
        ],
        fonts: [
            "font/ttf",                                // TTF
            "font/otf",                                // OTF
        ]
    }

    private getAcceptedMimeTypesAsList(): Array<string> {
        const types: Array<string> = []
        Object.keys(this.acceptedMimeTypes).forEach(key => {
            types.push(...this.acceptedMimeTypes[key])
        })
        return types;
    }

    static styles = css`
    :host {
        display: flex;
        flex-direction: column;
        height: 100%;
        overflow: auto;
        margin: 0;
        caret-color: transparent;
        background: rgba(0,0,0,0.05);
    }
    ast-renderer {
        overflow-y: scroll;
        flex-grow: 1;
    }
    .main-window {
        padding: 1em;
    }
    #docbook-output{
        height: 10em;
        overflow-y: scroll;
        margin: 1em 0;
    }
    .no-file, .container {
        height: 100%;
        text-align: center;
        background: white;
    }
    vaadin-upload {
        height: 100%;
        border: none;
    }
    vaadin-upload[dragover-valid] {
        background: rgba(0,0,0,0.05);
    }
    vaadin-upload::part(primary-buttons), .converting {
        max-width: 30em;
        margin: auto;
        margin-top: 10em;
        margin-bottom: 1em;
    }
    .detected-files {
        margin-top: 7em;
    }
    .detected-files h1 {
        font-size: 1.5em;
    }
    .detected-files h2 {
        font-size: 1em;
        margin-bottom: 0;
    }
    .detected-files h2::first-letter {
        text-transform: uppercase;
    }
    .detected-files p {
        margin: 0
    }
    `;

    connectedCallback() {
        super.connectedCallback();
        document.addEventListener("idml-loaded", this.handleFileSelectedFromHeader);
        document.addEventListener("delete-ast", this.deleteAst);
        document.addEventListener("export-doc", this.handleExport);
        document.addEventListener("export-converter", this.exportConverter);
        document.addEventListener("export-styles", this.exportStyles);
        document.addEventListener("rules-unchanged", this.exportConverterWithRules);
        document.addEventListener("load-sample", this.loadSample);
    }
    
    disconnectedCallback() {
        document.removeEventListener("idml-loaded", this.handleFileSelectedFromHeader);
        document.removeEventListener("delete-ast", this.deleteAst);
        document.removeEventListener("export-doc", this.handleExport);
        document.removeEventListener("export-converter", this.exportConverter);
        document.removeEventListener("export-styles", this.exportStyles);
        document.removeEventListener("rules-unchanged", this.exportConverterWithRules);
        document.removeEventListener("load-sample", this.loadSample);
        super.disconnectedCallback();
    }

    protected async firstUpdated() {
        if (isDebugModeOn()) {
            console.log("Debug: true");
            this.docbookOutput = __DOCBOOK_DEBUG_FILE__;
            this.setSharedCss(__CSS_DEBUG_FILE__);
            this.inputFormat = "idml"
            // this.inputFormat = "docbook"
            await this.docbook2pandocJson();
            await this.pandocJson2outDesignJson();
        }
        this.idml2docbookOptions = this.defaultIdml2docbookOptions;
        this.loadSessionFromLocalStorage()
    }
    
    render() {
        if (this.converting) {
            return this.renderConverting();
        }

        if (isDebugModeOn()) {
            return this.renderOutputOnly();
        }

        if (!this.file) {
            return this.renderNoFile();
        }

        return this.renderAst();
    }
    
    protected renderOutputOnly() {
        if (!this.outDesignAst) return html``;

        return html`
            <ast-renderer .originalAst=${this.outDesignAst}></ast-renderer>
            <od-footer></od-footer>
        `;
    }

    protected renderNoFile() {
        // upload-before is prevented because we do not need to send a request
        // as everything is handled directly by the client.
        return html`
            <div class="no-file">
                <vaadin-upload
                noAuto=${true}
                @upload-before=${(e: any) => e.preventDefault()}
                @files-changed="${this.handleUpload}"
                accept=${this.getAcceptedMimeTypesAsList().join(",")}>
                    Start by submitting an InDesign package, an IDML file,<br>or a document in any format you want 🤯
                </vaadin-upload>
            </div>
        `;
    }


    private handleUpload = (e: UploadFilesChangedEvent) => {
        const upload = e.target as any;
        const files = upload.files as File[];

        // clear previous timer
        if (this.uploadDebounceTimer) {
            clearTimeout(this.uploadDebounceTimer);
        }

        // debounce
        this.uploadDebounceTimer = window.setTimeout(() => {
            if (files) this.processFiles(files);
        }, 300);
    };

    private processFiles(files: Array<File>, avoidDisablingInDesignStyles: boolean = false) {

        if (!files.length) return;
        this.files = this.sortFilesByType(files)

        if(!this.files.documents.length) return;
        // At this point we know we have a file to process!

        let outDesignAstGenerated: Promise<void>;

        // PROCESS DOCUMENT
        const idmlFile = this.files.documents.find(f => f.name.endsWith(".idml"));

        if(idmlFile) {
            // Remove the webkitRelativePath
            // as it is not needed here, and it breaks server-side
            this.inputFormat = "idml"
            this.file = new File(
                [idmlFile],
                idmlFile.webkitRelativePath ? idmlFile.name : idmlFile.name,
                { type: idmlFile.type }
            );
            outDesignAstGenerated = this.idml2outdesign();
            this.enableInDesignStyles();
        } else {
            // There could be one of several files at this point...
            // Let's imagine there is only one.
            this.file = this.files.documents[0];
            let format: InputFormat;
            if(!this.file.type) {
                format = fileTypeToPandocType(this.file.name.split(".").splice(-1)[0]);
            } else format = fileTypeToPandocType(this.file.type);
            this.inputFormat = format;
            outDesignAstGenerated = this.arbitraryFormat2outdesign(format);
            if(!avoidDisablingInDesignStyles) this.disableInDesignStyles();
        }

        // MAP IMAGES
        if(this.files.images) {
            this.files.images.forEach(image => {
                const sanitized: string = sanitize(image.name)
                imagesObjectURLMap[sanitized] = {
                    filename: image.name,
                    url: URL.createObjectURL(image),
                    type: image.type,
                }
            })
        }

        if(this.files.fonts) loadFonts(this.files.fonts)

        outDesignAstGenerated.finally( () => {
            this.saveSessionToLocalStorage()
        })
    };

    protected renderConverting() {
        return html`
            <div class="container">
                <div class="converting">
                    <p><b>${this.file?.name} is being converted...</b><br />It might take some time ⏳<br/></p>
                    <!-- <wa-progress-bar indeterminate></wa-progress-bar> -->
                    <wa-progress-bar 
                        value=${this.conversionProgress >= 0 ? this.conversionProgress : nothing}
                        ?indeterminate=${this.conversionProgress === -1}
                    ${this.renderFilesList()}
                </div>
            </div>
        `;
    }

    protected sortFilesByType(files: Array<File>): Record<string, Array<File>> {
        const sortedFiles: Record<string, Array<File>> = {};
        const documentFiles: File[] = [];
        const imageFiles: File[] = [];
        const fontFiles: File[] = [];
        files.forEach((file) => {
            if(this.acceptedMimeTypes.documents.includes(file.type) || !file.type) documentFiles.push(file) 
            if(this.acceptedMimeTypes.images.includes(file.type)) imageFiles.push(file) 
            if(this.acceptedMimeTypes.fonts.includes(file.type)) fontFiles.push(file) 
        })
        if(documentFiles.length > 0) sortedFiles.documents = documentFiles.sort()
        if(imageFiles.length > 0) sortedFiles.images = imageFiles.sort()
        if(fontFiles.length > 0) sortedFiles.fonts = fontFiles.sort()
        return sortedFiles;
    }

    protected renderFilesList() {
        if (Object.keys(this.files as Record<string, Array<File>>).length < 1) return ;
        return html`
            <div class="detected-files">
                <h1>Detected media</h1>
                    ${Object.keys(this.files as Record<string, Array<File>>).map((key) => {
                        return html`
                            <h2>${key}</h2>
                            ${this.files?.[key].map((file) => {
                                return html`<p>${file.name}</p>`;
                            })}
                        `;
                    })}
            </div>
        `
    }

    protected renderAst() {
        return html`
            <ast-renderer
            .originalAst=${this.outDesignAst}>
            </ast-renderer>
            <od-footer></od-footer>
        `;
    }

    private handleFileSelectedFromHeader = (e: Event) => {
        const ev = e as CustomEvent<HTMLInputElement>;
        const fileList = ev.detail.files
        if (fileList) {
            const filesArray: File[] = new Array<File>();
            let i = 0;
            while (i < fileList.length) {
                if(fileList.item(i)) filesArray[i] = fileList.item(i) as File;
                i++;
            }
            this.processFiles(filesArray)
            this.idml2outdesign();
        }
    };

    private deleteAst = () => {
        this.file = undefined;
        this.docbookOutput = "";
        this.pandocJson = undefined;
        this.outDesignAst = undefined;
        this.saveOutDesignAstInLocalStorage()
    }

    private handleExport = async (e: Event) => {
        const ev = e as CustomEvent;
        this.outputFormat = ev.detail.to as unknown as OutputFormat;
        const ext = ev.detail.ext as unknown as OutputFormat;

        const astRenderer = this.renderRoot.querySelector("ast-renderer") as AstRenderer
        const ast = astRenderer.ast
        if (!ast) return;

        const pandocAst = outDesignToPandoc(ast);

        const result = await pandocServer(JSON.stringify(pandocAst), "json", this.outputFormat);
        if (!result) return;

        const baseName = this.file?.name?.replace(/\.[^/.]+$/, "") || "export";

        let blob: Blob;
        let filename: string;

        if (result instanceof Blob) {
            blob = result;
            filename = `${baseName}.${ext}`;
        } else {
            let text = result;
            if (this.outputFormat === "json") text = JSON.stringify(result)
            blob = new Blob([text as string], { type: "text/plain;charset=utf-8" });
            filename = `${baseName}.${ext}`;
        }

        const url = URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();

        // cleanup
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    private exportConverter = (e: Event) => {
        const ev = e as CustomEvent;
        this.outputFormat = ev.detail as unknown as OutputFormat;
        this.dispatchEvent(
            new CustomEvent("rules-request", {
                detail: { changes: false },
                bubbles: true,
                composed: true
            })
        );
    }

    private exportStyles = () => {
        let blob: Blob = new Blob([stringifyStyleSheet(this.cssOutput)], { type: "text/css"} )

        const url = URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = url;
        a.download = this.file ? this.file.name.split("/").pop()?.split(".")[0] + ".css" : "styles.css"
        document.body.appendChild(a);
        a.click();

        // cleanup
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }

    private exportConverterWithRules = async (e: Event) => {
        const ev = e as CustomEvent;
        const rules = ev.detail;

        if(!this.inputFormat) throw new Error("No input format available for generating the converter.")
        if(!this.file) throw new Error("No input file was loaded!")
        generateConverter(this.inputFormat, this.outputFormat, this.file, rules, this.idml2docbookOptions)
    }

    private loadSample = async (e: Event) => {
        const ev = e as CustomEvent;
        const sampleName = ev.detail
        const manifest = await getManifest();

        // Load document
        try {
            const filepath: string = manifest.samples[sampleName].doc
            const ext = filepath.split(".").pop()
            const filename: string = `${filepath.split("/")[1]}.${ext}` 
            if(ext === "dbk") {
                this.docbookOutput = await (await fetch(filepath)).text()
                this.idml2docbookOptions = this.defaultIdml2docbookOptions
                const blob = new Blob([this.docbookOutput]);
                this.file = new File([blob], filename, { type: 'application/x-docbook+xml' });
                this.processFiles([this.file as File], true)
                this.enableInDesignStyles()
            } else if (ext === "idml") {
                const fetchedData = await (await fetch(filepath)).blob()
                this.idml2docbookOptions = this.defaultIdml2docbookOptions
                const blob = new Blob([fetchedData]);
                this.file = new File([blob], filename);
                this.processFiles([this.file as File])
            } else {
                // If it's not docbook, then it's markdown
                const markdown = await (await fetch(filepath)).text()
                const blob = new Blob([markdown]);
                this.file = new File([blob], filename, { type: 'text/markdown' });
                this.processFiles([this.file as File])
            }
        } catch (error) {
            console.error("Failed to load sample document:", sampleName, error);
        }

        // Load rules
        try {
            const filepath = manifest.samples[sampleName].rules
            const rules = await (await fetch(filepath)).json()
            this.dispatchEvent(
                new CustomEvent("json-loaded-from-sample", {
                    detail: rules,
                    bubbles: true,
                    composed: true
                })
            );
        } catch (error) {
            console.error("Failed to load sample rules:", sampleName, error);
        }

        // Load CSS (not on all samples)
        try {
            if(manifest.samples[sampleName].styles) {
                const css = await (await fetch(manifest.samples[sampleName].styles)).text()
                this.setSharedCss(css)
            }
        } catch (error) {
            console.error("Failed to load sample styles:", sampleName, error);
        }
    }

    private async idml2outdesign() {
        this.converting = true;
        this.conversionTimeStart = performance.now();
        this.conversionProgress = 0

        // Start a timer to update progress
        const updateProgress = () => {
            this.conversionProgress = this.getEstimatedConversionTime();
            if (this.conversionProgress < 100 && this.converting) {
                requestAnimationFrame(updateProgress);
            }
        };
        requestAnimationFrame(updateProgress);

        console.log("Converting IDML...");
        await this.idml2docbook();
        console.log("And now using Pandoc...")
        await this.docbook2pandocJson();
        await this.pandocJson2outDesignJson();
        this.converting = false;
        this.conversionProgress = -1;
    }

    private getEstimatedConversionTime(): number {
        if (!this.file) return -1 ;
        
        // A linear regression was performed on this empirical data:
        // Déborder Bolloré was clearly an outlier, and was ignored...
        
        //     name     | size (B) | time (ms)
        //     ===============================
        //     hello    |    34290 |     16000
        //     indesign |   136499 |     20000
        //     voix     |   267572 |     25000
        //     lmep     |   416364 |     30000
        //     allinc   |   821008 |     77000
        //     bollo    | 27683361 |    317000

        const a = 0.078
        const b = 7500

        const estimatedDuration = this.file?.size * a + b;
        const elapsedTime = performance.now() - this.conversionTimeStart

        return elapsedTime / estimatedDuration * 100 ;
    }
    
    private async idml2docbook() {
        const result = await idml2docbook(this.file as File, this.idml2docbookOptions!);
        if(!result) throw Error;
        console.log(result)
        this.docbookOutput = result.output;
        this.idml2docbookOptions = result.options as Record<string, string | boolean>;
        this.setSharedCss(result.css)
    }
    
    private async docbook2pandocJson() {
        this.pandocJson = await pandocServer(this.docbookOutput, "docbook", "json") as PandocJson ;
    }

    private async pandocJson2outDesignJson() {
        this.outDesignAst = await pandocToOutDesign(this.pandocJson as PandocJson)
        this.outDesignAst = rolesToClasses(this.outDesignAst)
    }

    private async arbitraryFormat2outdesign(format: InputFormat) {
        this.converting = true;
        this.pandocJson = await pandocServer(this.file as File, format, "json") as PandocJson ;
        await this.pandocJson2outDesignJson()
        this.converting = false;
    }

    private async setSharedCss(sheet: string) {
        this.cssOutput.replaceSync(sheet);
        setSharedStyles(this.cssOutput, "data-indesign-style")
    }

    private async enableInDesignStyles() {
        this.dispatchEvent(
            new CustomEvent("enable-indesign-styles", {
                bubbles: true,
                composed: true
            })
        );
    }

    private async disableInDesignStyles() {
        this.dispatchEvent(
            new CustomEvent("disable-indesign-styles", {
                bubbles: true,
                composed: true
            })
        );
    }

    private loadSessionFromLocalStorage() {
        this.outDesignAst = this.loadOutDesignAstFromLocalStorage();
        if(!this.outDesignAst) return;
        if(localStorage.getItem("filename")) {
            this.file = new File(
                [new Blob()],
                localStorage.getItem("filename") as string,
                { type: localStorage.getItem("type") as string}
            )
            if (!this.file.name.endsWith(".idml")) this.disableInDesignStyles()
        }
        if(localStorage.getItem("css")) {
            this.setSharedCss(localStorage.getItem("css") as string);
        }
    }

    private loadOutDesignAstFromLocalStorage(): OutDesignAST | undefined {
        const localAst = localStorage.getItem("outdesignAst");

        if (!localAst || localAst === "") {
            console.warn("No outdesignAst found in localStorage");
            return undefined;
        }

        try {
            const parsed = JSON.parse(localAst);

            if (!Array.isArray(parsed)) {
                console.warn("Invalid format in localStorage (expected array)");
                return undefined;
            }

            const retrievedTypedArray = new Uint8Array(parsed);
            const jsonString = pako.inflate(retrievedTypedArray, { to: "string" });

            const loadedOutDesignAst = JSON.parse(jsonString);
            console.log("Document loaded from local storage.", loadedOutDesignAst)
            return loadedOutDesignAst;
        } catch (e) {
            console.error("Failed to parse or inflate outdesignAst:", e);
            console.error("Raw value was:", localAst.slice(0, 200));
            return undefined;
        }
    }

    private saveSessionToLocalStorage() {
        this.saveOutDesignAstInLocalStorage()

        if(this.file) {
            localStorage.setItem("filename", this.file.name)
            localStorage.setItem("type", this.file.type)
        }

        if(this.cssOutput) {
            localStorage.setItem("css", stringifyStyleSheet(this.cssOutput))
        }
    }

    private saveOutDesignAstInLocalStorage() {
        if (!this.outDesignAst) {
            localStorage.setItem('outdesignAst', "");
            return;
        };
        const jsonString = JSON.stringify(this.outDesignAst);
        const compressed = pako.deflate(jsonString);
        const compressedStr = JSON.stringify(Array.from(compressed));
        // Here, it's a kinda ugly way to store locally a 5Mo file,
        // but it will work for most books and does not need more code than that
        // so that's nice.
        // https://stackoverflow.com/questions/52419694/how-to-store-uint8array-in-the-browser-with-localstorage-using-javascript
        localStorage.setItem('outdesignAst', compressedStr);
    }
}

